import React from "react";

import {
  Button,
  Iconography,
  IconographySizes,
  SearchInput,
} from "@hid-galaxy-ui/galaxy-react";

import { useNavigate } from "react-router-dom";

import { PartnerEcosystemGridIcon } from "../shared/adminHomeIcons";
import {
  SHOW_PARTNER_ECOSYSTEM_HEADER_ACTIONS,
  SHOW_PARTNER_ECOSYSTEM_REPORT_LINK,
  DASHBOARD_REPORT_LINKS_DISABLED,
} from "../../constants/dashboardVisualConfig";
import type { PartnerEcosystemData } from "../../types";

const SUMMARY_BASE_MIN_HEIGHT = 324;

type PartnerEcosystemCardProps = {
  data: PartnerEcosystemData;
  dimensionColumnLabel?: string;
  showCreatePartner?: boolean;
  createPartnerDisabled?: boolean;
  onCreatePartner?: () => void;
  onManagePartners?: () => void;
  showHeaderActions?: boolean;
  showReportLink?: boolean;
};

const PartnerEcosystemCard: React.FC<PartnerEcosystemCardProps> = ({
  data,
  dimensionColumnLabel = "Revenue Stream",
  showCreatePartner = false,
  createPartnerDisabled = false,
  onCreatePartner,
  onManagePartners,
  showHeaderActions = SHOW_PARTNER_ECOSYSTEM_HEADER_ACTIONS,
  showReportLink = SHOW_PARTNER_ECOSYSTEM_REPORT_LINK,
}) => {
  const { totalPartners, activePartners, regions } = data;
  const [search, setSearch] = React.useState("");
  const tableWrapRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLTableElement>(null);
  const [summaryMinHeight, setSummaryMinHeight] = React.useState(
    SUMMARY_BASE_MIN_HEIGHT
  );
  const isSummaryExpanded = summaryMinHeight > SUMMARY_BASE_MIN_HEIGHT;

  const filteredRegions = React.useMemo(() => {
    if (!showHeaderActions) {
      return regions;
    }

    const query = search.trim().toLowerCase();
    if (!query) {
      return regions;
    }

    return regions.filter((row) => {
      const fields = [
        row.region,
        row.active,
        row.registered,
        row.lapsed,
        row.inactive,
        row.total,
      ];

      return fields.some((field) =>
        String(field).toLowerCase().includes(query)
      );
    });
  }, [regions, search, showHeaderActions]);

  const navigate = useNavigate();

  const handleRevenueStreamClick = (value: string) => {
    navigate("/hid-admin/dashboard/partner-organizations", {
      state: {
        revenueStreamFilter: value,
      },
    });
  };

  React.useLayoutEffect(() => {
    const table = tableRef.current;
    const tableWrap = tableWrapRef.current;
    if (!table || !tableWrap) {
      return;
    }

    const syncSummaryHeight = () => {
      setSummaryMinHeight(
        Math.max(SUMMARY_BASE_MIN_HEIGHT, table.offsetHeight)
      );
    };

    syncSummaryHeight();

    const resizeObserver = new ResizeObserver(syncSummaryHeight);
    resizeObserver.observe(tableWrap);

    return () => resizeObserver.disconnect();
  }, [filteredRegions]);

  const columnTotals = React.useMemo(() => {
    return filteredRegions.reduce(
      (acc, row) => ({
        total: acc.total + Number(row.total || 0),
        active: acc.active + Number(row.active || 0),
        registered: acc.registered + Number(row.registered || 0),
        lapsed: acc.lapsed + Number(row.lapsed || 0),
        inactive: acc.inactive + Number(row.inactive || 0),
      }),
      {
        total: 0,
        active: 0,
        registered: 0,
        lapsed: 0,
        inactive: 0,
      }
    );
  }, [filteredRegions]);

  return (
    <section className="admin-home-ecosystem">
      <div className="admin-home-ecosystem__header">
        <div className="admin-home-ecosystem__title-block">
          <span className="admin-home-ecosystem__header-icon">
            <PartnerEcosystemGridIcon width={24} height={24} />
          </span>

          <div className="admin-home-ecosystem__header-text">
            <h3 className="admin-home-ecosystem__title">Partner Ecosystem</h3>

            <p className="admin-home-ecosystem__subtitle">
              Overview &amp; Regional Breakdown
            </p>
          </div>
        </div>

        {showHeaderActions ? (
          <div className="admin-home-ecosystem__toolbar">
            <div className="admin-home-ecosystem__search-wrap">
              <SearchInput
                id="admin-home-partner-search"
                name="admin-home-partner-search"
                theme="dark"
                placeholder={`Filter by ${dimensionColumnLabel.toLowerCase()} or count...`}
                isDisplayClearIcon={true}
                value={search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearch(e.target.value)
                }
                onSearch={(_e: unknown, value: string) => setSearch(value)}
              />
            </div>
            {showCreatePartner ? (
              <Button
                variant="primary"
                label="Create Partner"
                icon={<Iconography icon="plus" size={IconographySizes.Small} />}
                disabled={createPartnerDisabled}
                onClick={onCreatePartner}
                className="admin-home-ecosystem__create-btn"
              />
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="admin-home-ecosystem__main">
        <div className="admin-home-ecosystem__columns">
          <div
            className={`admin-home-ecosystem__summary${
              isSummaryExpanded
                ? " admin-home-ecosystem__summary--expanded"
                : ""
            }`}
            style={{ minHeight: summaryMinHeight }}
          >
            <div className="admin-home-ecosystem__summary-main">
              <div className="admin-home-ecosystem__summary-metric">
                <div className="admin-home-ecosystem__summary-heading">
                  <Iconography
                    icon="userGroup"
                    size={IconographySizes.Small}
                    className="admin-home-ecosystem__summary-icon"
                  />
                  <span className="admin-home-ecosystem__summary-label">
                    <span>Active</span>
                    <span>Partners</span>
                  </span>
                </div>

                <span className="admin-home-ecosystem__summary-value">
                  <span className="admin-home-ecosystem__summary-value-active">
                    {activePartners.toLocaleString("en-US")}
                  </span>
                  <span className="admin-home-ecosystem__summary-value-separator">
                    /
                  </span>
                  <span className="admin-home-ecosystem__summary-value-total">
                    {totalPartners.toLocaleString("en-US")}
                  </span>
                </span>
              </div>
            </div>

            <button
              type="button"
              className="admin-home-ecosystem__manage"
              onClick={onManagePartners}
            >
              Manage Partners
            </button>
          </div>

          <div className="admin-home-ecosystem__table-wrap" ref={tableWrapRef}>
            <table className="admin-home-ecosystem__table" ref={tableRef}>
              <thead>
                <tr>
                  <th>{dimensionColumnLabel}</th>

                  <th>Total</th>

                  <th>Active</th>

                  <th>Registered</th>

                  <th>Lapsed</th>

                  <th>Inactive</th>
                </tr>
              </thead>

              <tbody>
                {filteredRegions.length > 0 ? (
                  filteredRegions.map((row) => (
                    <tr
                      key={row.region}
                      className="admin-home-ecosystem__row"
                      onClick={() => handleRevenueStreamClick(row.region)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        <span className="admin-home-ecosystem__region-cell">
                          <span
                            className="admin-home-ecosystem__region-icon"
                            style={{ backgroundColor: row.accentColor }}
                          >
                            <Iconography
                              icon="globe"
                              size={IconographySizes.Small}
                            />
                          </span>
                          <span className="admin-home-ecosystem__region-name">
                            {row.region}
                          </span>
                        </span>
                      </td>
                      <td className="admin-home-ecosystem__total">
                        {row.total}
                      </td>
                      <td>{row.active}</td>
                      <td>{row.registered}</td>
                      <td>{row.lapsed}</td>
                      <td className="admin-home-ecosystem__inactive">
                        {row.inactive}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="admin-home-ecosystem__empty">
                      {showHeaderActions && search.trim()
                        ? `No ${dimensionColumnLabel.toLowerCase()} rows match your filter.`
                        : `No ${dimensionColumnLabel.toLowerCase()} data available.`}
                    </td>
                  </tr>
                )}
              </tbody>

              <tfoot>
                <tr className="admin-home-ecosystem__footer-row">
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td>
                    <strong>{columnTotals.total}</strong>
                  </td>
                  <td>
                    <strong>{columnTotals.active}</strong>
                  </td>
                  <td>
                    <strong>{columnTotals.registered}</strong>
                  </td>
                  <td>
                    <strong>{columnTotals.lapsed}</strong>
                  </td>
                  <td>
                    <strong>{columnTotals.inactive}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {showReportLink ? (
          <div className="admin-home-ecosystem__table-footer">
            <button
              type="button"
              className={`admin-home-ecosystem__manage${
                DASHBOARD_REPORT_LINKS_DISABLED
                  ? " admin-home-ecosystem__manage--disabled"
                  : ""
              }`}
              onClick={
                DASHBOARD_REPORT_LINKS_DISABLED ? undefined : onManagePartners
              }
              disabled={DASHBOARD_REPORT_LINKS_DISABLED}
            >
              View Partner Ecosystem Report
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default PartnerEcosystemCard;
